import { useRoomToken } from '../state/roomToken';
import { cx, playerColor } from './tokens';

export interface AvatarSubject {
  id: string;
  name: string;
  initials: string;
  colorIndex: number;
  hasAvatar: boolean;
  avatarVersion: number;
}

export type AvatarSize = 'sm' | 'md' | 'lg' | 'hero';

const SIZES: Record<AvatarSize, string> = {
  sm: 'h-10 w-10 text-[0.7rem] border-2',
  md: 'h-16 w-16 text-base border-[3px]',
  lg: 'h-24 w-24 text-2xl border-[3px]',
  hero: 'h-[15cqh] w-[15cqh] min-h-8 min-w-8 text-[4.4cqh] border-4',
};

export function avatarUrl(roomToken: string, subject: AvatarSubject): string {
  return `/api/rooms/${roomToken}/avatars/${subject.id}?v=${subject.avatarVersion}`;
}

/**
 * A player's face. Photo when they supplied one, otherwise initials on their
 * uniquely assigned colour — unique names plus unique colours make every
 * placeholder distinguishable by construction.
 */
export function Avatar({
  subject,
  roomToken,
  size = 'md',
  dimmed = false,
  className,
}: {
  subject: AvatarSubject;
  /** Omit to take the room token from context. */
  roomToken?: string | null;
  size?: AvatarSize;
  dimmed?: boolean;
  className?: string;
}) {
  const contextToken = useRoomToken();
  const token = roomToken === undefined ? contextToken : roomToken;
  const showPhoto = subject.hasAvatar && token !== null;

  return (
    <div
      className={cx(
        'relative shrink-0 overflow-hidden border-ink bg-paper-sunk',
        SIZES[size],
        dimmed && 'opacity-45 grayscale',
        className,
      )}
      style={showPhoto ? undefined : { backgroundColor: playerColor(subject.colorIndex) }}
      aria-label={subject.name}
    >
      {showPhoto ? (
        <img
          src={avatarUrl(token!, subject)}
          alt={subject.name}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center font-display text-paper-raised">
          {subject.initials}
        </span>
      )}
    </div>
  );
}
